// @ts-nocheck
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '@way/theme-host/services/logger.service';
import { CommonModule } from '@angular/common';
import { PackageService } from '@way/theme-host/services/package.service';
import { Package } from '@way/theme-host/shared/types';
import {
  ChampsPageHeaderComponent,
  NavigationItem,
  Logo,
  CTAButton,
} from '../shared/page-header/page-header.component';
import { ChampsPageFooterComponent } from '../shared/footer/footer.component';
import {
  SharedPackageSelectionModalComponent,
  SelectedPackage,
} from '@way/website-themes/themes/shared/package-selection-modal/package-selection-modal.component';
import { ChampsMembershipCardComponent } from '../shared/membership-card/membership-card.component';
import { PACKAGE_ICON_FALLBACK } from '@way/theme-host/shared/constants/website-builder-assets';
import { resolveFooterQuickLinks } from '@way/theme-host/shared/utils/footer-quick-links.util';

// Import WebsiteElement type from home-page-preview
export interface WebsiteElement {
  id: string;
  type:
    | 'header'
    | 'hero'
    | 'section'
    | 'testimonials'
    | 'services'
    | 'booking'
    | 'contact'
    | 'footer'
    | 'packages'
    | 'coupons'
    | 'fleets'
    | 'express-details';
  content: any;
  selected?: boolean;
}

@Component({
  selector: 'app-champs-memberships-preview',
  standalone: true,
  imports: [
    CommonModule,
    ChampsPageHeaderComponent,
    ChampsPageFooterComponent,
    SharedPackageSelectionModalComponent,
    ChampsMembershipCardComponent,
  ],
  templateUrl: './memberships-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./memberships-preview.component.scss'],
})
export class ChampsMembershipsPreviewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() pageData: any = null;
  @Input() sectionVisibility: Record<string, boolean> = {};
  @Input() pages: any[] = [];
  @Input() currentPageType = '';
  @Input() elements: WebsiteElement[] = [];
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Input() primaryColor: string | null = null; // Primary color from theme
  @Input() headingColor: string | null = null; // Heading color from branding
  @Input() paragraphColor: string | null = null; // Paragraph/description color from branding
  @Input() headingFont: string | null = null; // Heading font from branding
  @Input() bodyFont: string | null = null; // Body font from branding
  @Output() elementSelect = new EventEmitter<WebsiteElement>();
  @Output() navigationClick = new EventEmitter<string>();

  membershipPlans: Package[] = [];
  comparisonData: any = null;
  loading = false;
  error: string | null = null;

  // Package selection modal
  showPackageSelectionModal = false;
  selectedPackageForModal: Package | null = null;

  // Page data from JSON
  headerData: any = null;
  membershipPlansSectionData: any = null;
  comparisonSectionData: any = null;
  footerData: any = null;
  pageTitleData: any = null;

  // Loading and animation state
  isPageLoaded = false;
  private intersectionObserver?: IntersectionObserver;
  private animatedElements = new Set<Element>();

  constructor(
    private logger: LoggerService,
    private packageService: PackageService,
    private cdr: ChangeDetectorRef
  ) {}

  /**
   * Initialize component on init: load page content, set loading state, and trigger scroll animations after brief delays.
   * @example
   * gOnInit()
   * undefined
   * @returns {void} Performs side effects (loads content, updates isPageLoaded, calls change detection, and initializes scroll animations).
   */
  ngOnInit(): void {
    this.loadPageContent();

    // Initialize page loading state
    this.isPageLoaded = false;

    // Simulate page load animation
    setTimeout(() => {
      this.isPageLoaded = true;
      this.cdr.detectChanges();
      // Initialize scroll animations after page loads
      setTimeout(() => this.initScrollAnimations(), 100);
    }, 300);
  }

  ngOnDestroy(): void {
    // Clean up intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    this.animatedElements.clear();
  }

  /**
   * Initialize scroll animations using Intersection Observer
   */
  private initScrollAnimations(): void {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return; // Fallback for browsers without IntersectionObserver
    }

    // Use requestAnimationFrame for smoother performance
    requestAnimationFrame(() => {
      // Create intersection observer for scroll animations with optimized settings
      this.intersectionObserver = new IntersectionObserver(
        entries => {
          // Use requestAnimationFrame to batch DOM updates
          requestAnimationFrame(() => {
            entries.forEach(entry => {
              if (entry.isIntersecting && !this.animatedElements.has(entry.target)) {
                entry.target.classList.add('animate-in');
                this.animatedElements.add(entry.target);
                // Unobserve after animation to improve performance
                this.intersectionObserver?.unobserve(entry.target);
              }
            });
          });
        },
        {
          threshold: [0.1, 0.2], // Multiple thresholds for smoother detection
          rootMargin: '0px 0px -80px 0px', // Reduced margin for better scroll performance
        }
      );

      // Observe all animatable elements with delay to avoid blocking initial render
      setTimeout(() => {
        const elements = document.querySelectorAll('.memberships-preview .website-element');
        elements.forEach(el => {
          // Check if element is already in viewport
          const rect = el.getBoundingClientRect();
          const isInViewport = rect.top < window.innerHeight + 100 && rect.bottom > -100;

          el.classList.add('scroll-animate');

          // If element is already visible, animate it immediately
          if (isInViewport) {
            requestAnimationFrame(() => {
              el.classList.add('animate-in');
              this.animatedElements.add(el);
            });
          } else {
            // Only observe if not already in viewport
            this.intersectionObserver?.observe(el);
          }
        });
      }, 300);
    });
  }

  /**
   * Responds to Angular input changes: reloads page content, updates header navigation, and refreshes membership plan section data when relevant inputs change.
   * @example
   * gOnChanges({ pageData: SimpleChange, pages: SimpleChange, currentPageType: SimpleChange, elements: SimpleChange })
   * void
   * @param {{SimpleChanges}} {{changes}} - Object mapping input names to SimpleChange instances describing current and previous values.
   * @returns {{void}} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageData'] && !changes['pageData'].firstChange) {
      this.loadPageContent();
    }
    // Update navigation when pages or currentPageType changes
    if ((changes['pages'] || changes['currentPageType']) && this.headerData) {
      this.updateHeaderNavigation();
    }
    // Reload page content when elements change (for background color updates)
    if (changes['elements'] && !changes['elements'].firstChange) {
      // Force reload of membershipPlansSectionData to get updated background color
      const sections = this.pageData?.page?.sections || this.pageData?.sections || {};
      this.membershipPlansSectionData = sections.membership_plans_section || {};
    }
  }

  /**
   * Load and initialize the Memberships preview page content: extract sections from pageData (supports editor and preview formats), populate component section properties, update header navigation, and trigger loading of membership plans and comparison data.
   * @example
   * this.loadPageContent()
   * void
   * @returns {{void}} Does not return a value; updates component state and triggers loading of related API data.
   **/
  private loadPageContent(): void {
    if (!this.pageData) return;

    this.logger.debug('📦 Loading Memberships page content:', this.pageData);

    // Extract sections from page data - handle both editor and preview formats
    // Editor format: pageData.page.sections
    // Preview format: pageData.json_data.page.sections
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    const sections = page?.sections || this.pageData?.sections || {};

    this.headerData = sections.header || {};
    this.membershipPlansSectionData = sections.membership_plans_section || {};
    this.comparisonSectionData = sections.comparison_section || {};
    this.footerData = sections.footer || {};
    // Load page-title section (for heading and description)
    this.pageTitleData = sections['page-title'] || sections.hero_section || {};

    this.logger.debug('Header data:', this.headerData);
    this.logger.debug('Membership plans section data:', this.membershipPlansSectionData);
    this.logger.debug('Comparison section data:', this.comparisonSectionData);
    this.logger.debug('Footer data:', this.footerData);
    this.logger.debug('Page title data:', this.pageTitleData);

    // Update header navigation to match current pages
    this.updateHeaderNavigation();

    // Load membership plans from API
    this.loadMembershipPlans();
    // Load comparison data from API
    this.loadComparisonData();
  }

  /**
   * Update the headerData.navigation from this.pages (exclude pages with visible === false) and ensure a default CTA button exists on headerData if missing.
   * @example
   * updateHeaderNavigation()
   * undefined
   * @returns {void} Updates this.headerData in-place; does not return a value.
   **/
  private updateHeaderNavigation(): void {
    if (!this.headerData) this.headerData = {};

    // Generate navigation from pages if pages are provided
    // Filter out inactive pages (visible: false means active: false)
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      this.headerData.navigation = activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        active: page.pageType === this.currentPageType,
      }));
      this.logger.debug('Updated header navigation for Memberships:', this.headerData.navigation);
    }

    // Ensure CTA button exists
    if (!this.headerData.ctaButton && !this.headerData.button) {
      this.headerData.ctaButton = {
        text: '',
        link: '#booking',
        backgroundColor: '#1B5BFF',
        textColor: '#FFFFFF',
      };
    }
  }

  /**
   * Load membership plans from the package service, filter to only include packages with 'monthly_membership' in wash_types, update component state, and generate comparison data.
   * @example
   * this.loadMembershipPlans()
   * // After completion: this.membershipPlans is populated (or this.error is set) and this.loading is false.
   * @returns {void} Updates internal component state (membershipPlans, loading, error) and does not return a value.
   **/
  private loadMembershipPlans(): void {
    this.loading = true;
    this.error = null;

    // Use the same API as express washes
    this.packageService.getCarWashPackages().subscribe({
      next: response => {
        this.logger.debug('✅ Packages loaded for memberships:', response);
        if (response.status && response.data) {
          // Filter packages to only show those with monthly_membership in wash_types
          const allPackages = response.data;
          this.membershipPlans = allPackages.filter((pkg: Package) => {
            // Check if wash_types array includes 'monthly_membership'
            return (
              pkg.wash_types &&
              Array.isArray(pkg.wash_types) &&
              pkg.wash_types.includes('monthly_membership')
            );
          });
          this.logger.debug('Total packages:', allPackages.length);
          this.logger.debug('Membership plans (filtered):', this.membershipPlans.length);
          this.logger.debug('Filtered membership plans:', this.membershipPlans);

          if (this.membershipPlans.length === 0) {
            this.error = 'No membership plans available';
          } else {
            // Generate comparison data from the filtered membership plans
            this.generateComparisonData();
          }
        } else {
          this.error = 'No packages available';
        }
        this.loading = false;
      },
      error: err => {
        this.logger.error('❌ Error loading membership plans:', err);
        this.error = 'Failed to load membership plans';
        this.loading = false;
      },
    });
  }

  private loadComparisonData(): void {
    // Generate comparison data from membership plans
    // This will be called after membership plans are loaded
    // The comparison features will be extracted from the plans' features
    this.generateComparisonData();
  }

  /**
   * Generate comparison data from membership plans
   * Extracts all unique features from the membership plans
   */
  private generateComparisonData(): void {
    if (!this.membershipPlans || this.membershipPlans.length === 0) {
      this.comparisonData = null;
      return;
    }

    // Collect all unique features from all membership plans
    const allFeatures = new Set<string>();
    this.membershipPlans.forEach((plan: Package) => {
      if (plan.features && Array.isArray(plan.features)) {
        plan.features.forEach((feature: string) => {
          if (feature && typeof feature === 'string') {
            allFeatures.add(feature);
          }
        });
      }
    });

    // Convert to array of feature objects for comparison table
    this.comparisonData = Array.from(allFeatures).map((feature: string) => ({
      name: feature,
      key: feature.toLowerCase().replace(/\s+/g, '_'),
      type: 'text',
    }));

    this.logger.debug('✅ Comparison data generated from membership plans:', this.comparisonData);
  }

  // Format price for display
  formatPrice(price: number | string | undefined): string {
    if (!price) return '$0.00';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `$${numPrice.toFixed(2)}`;
  }

  // Get package image or fallback
  getPackageImage(pkg: Package): string {
    // Use image from API if available, otherwise fall back to static placeholder
    if (pkg.image && pkg.image.trim() !== '') {
      return pkg.image;
    }
    return PACKAGE_ICON_FALLBACK;
  }

  // Get package image fit/scaletype
  /**
   * Determine the CSS object-fit value for a package image based on the membership plan's scaletype, defaulting to 'cover'.
   * @example
   * etPackageImageFit({ id: 'basic' })
   * 'scale-down'
   * @param {{Package}} {{pkg}} - Package object containing at least an id used to lookup the plan configuration.
   * @returns {{string}} Return a CSS object-fit value: 'fill', 'cover', 'contain', or 'scale-down' (defaults to 'cover').
   **/
  getPackageImageFit(pkg: Package): string {
    // Check membership_plans_section.plans array for this plan's scaletype
    const membershipPlans = this.membershipPlansSectionData?.plans || [];
    const packageConfig = membershipPlans.find((p: any) => p.id === pkg.id);

    if (packageConfig?.scaletype) {
      // Map API scaletype to CSS object-fit values
      const scaletype = packageConfig.scaletype;
      if (scaletype === 'fill' || scaletype === 'stretch') {
        return 'fill';
      } else if (scaletype === 'cover') {
        return 'cover';
      } else if (scaletype === 'contain') {
        return 'contain';
      } else if (scaletype === 'fit') {
        return 'scale-down';
      }
    }

    // Default to cover if not specified
    return 'cover';
  }

  // Handle image loading errors
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = PACKAGE_ICON_FALLBACK;
  }

  // Check if package has unlimited washes
  isUnlimited(pkg: Package): boolean {
    return pkg.type === 'unlimited' || pkg.package_name?.toLowerCase().includes('unlimited');
  }

  // Get package type label
  getPackageTypeLabel(pkg: Package): string {
    return pkg.type === 'unlimited' ? 'Unlimited' : 'Single';
  }

  // Get display price (use single_list_price or monthly_list_price)
  getListPrice(pkg: Package): string {
    return pkg.type === 'unlimited' ? pkg.monthly_list_price : pkg.single_list_price;
  }

  // Get strikeoff price (discounted price)
  getStrikeoffPrice(pkg: Package): string {
    return pkg.type === 'unlimited' ? pkg.monthly_strikeoff_price : pkg.single_strikeoff_price;
  }

  /**
   * Check if a section should be visible based on section visibility settings
   */
  isSectionVisible(sectionKey: string): boolean {
    if (!this.sectionVisibility || Object.keys(this.sectionVisibility).length === 0) {
      return true;
    }
    return this.sectionVisibility[sectionKey] !== false;
  }

  /**
   * Get package section element for selection
   */
  getPackageSectionElement(): WebsiteElement | undefined {
    return this.elements.find(
      el =>
        el.type === 'packages' ||
        el.id === 'packages' ||
        (el as any).originalKey === 'package_section'
    );
  }

  /**
   * Get package section background color
   */
  getPackageSectionBackgroundColor(): string | null {
    // Priority 1: Check element content (most up-to-date, updated immediately)
    const packageElement = this.getPackageSectionElement();
    const packageContent = packageElement?.content as any;
    if (packageContent?.backgroundColor) {
      return packageContent.backgroundColor;
    }
    // Priority 2: Check membershipPlansSectionData (from API/pageData)
    if (this.membershipPlansSectionData?.backgroundColor) {
      return this.membershipPlansSectionData.backgroundColor;
    }
    // Priority 3: Check page-level theme background color
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    if (page?.theme?.background_color) {
      return page.theme.background_color;
    }
    // Priority 4: Check pageData directly
    const sections = page?.sections || this.pageData?.sections || {};
    const packageSection = sections.package_section || {};
    if (packageSection.backgroundColor) {
      return packageSection.backgroundColor;
    }
    // Default fallback
    return null;
  }

  /**
   * Get membership plans section background color
   */
  getMembershipPlansBackgroundColor(): string | null {
    if (this.membershipPlansSectionData?.layout?.background_color) {
      return this.membershipPlansSectionData.layout.background_color;
    }
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    if (page?.layout?.theme?.background_color) {
      return page.layout.theme.background_color;
    }
    return '#F5F5F5';
  }

  /**
   * Get membership plans title
   */
  getMembershipPlansTitle(): string {
    if (this.membershipPlansSectionData?.title?.label) {
      return this.membershipPlansSectionData.title.label;
    } else if (this.membershipPlansSectionData?.title) {
      return this.membershipPlansSectionData.title;
    }
    return 'Choose the Plan That Fits Your Ride';
  }

  /**
   * Get membership plans subtitle
   */
  getMembershipPlansSubtitle(): string {
    if (this.membershipPlansSectionData?.subtitle?.label) {
      return this.membershipPlansSectionData.subtitle.label;
    } else if (this.membershipPlansSectionData?.subtitle) {
      return this.membershipPlansSectionData.subtitle;
    }
    return 'Keep your car spotless all year with a plan that matches your lifestyle. Whether you wash once a week or every day, our memberships make it simple and affordable.';
  }

  /**
   * Get membership plans title color
   */
  getMembershipPlansTitleColor(): string {
    if (this.membershipPlansSectionData?.title?.style?.text_color) {
      return this.membershipPlansSectionData.title.style.text_color;
    }
    return this.getHeadingColor();
  }

  /**
   * Get membership plans subtitle color
   */
  getMembershipPlansSubtitleColor(): string {
    if (this.membershipPlansSectionData?.subtitle?.style?.text_color) {
      return this.membershipPlansSectionData.subtitle.style.text_color;
    }
    return this.getParagraphColor();
  }

  /**
   * Get membership plans title styles
   */
  getMembershipPlansTitleStyles(): any {
    if (this.membershipPlansSectionData?.title?.style) {
      const style = this.membershipPlansSectionData.title.style;
      const styles: any = {};
      if (style.font_size) styles['font-size'] = style.font_size;
      if (style.font_weight) styles['font-weight'] = style.font_weight;
      if (style.text_align) styles['text-align'] = style.text_align;
      return styles;
    }
    return {};
  }

  /**
   * Get plan name (BASIC, PREMIUM, PLATINUM)
   */
  getPlanName(plan: Package): string {
    if (plan.package_name) {
      return plan.package_name.toUpperCase();
    }
    return 'PLAN';
  }

  /**
   * Get plan price (monthly membership price)
   */
  getPlanPrice(plan: Package): string {
    if (plan.monthly_list_price) {
      const price =
        typeof plan.monthly_list_price === 'string'
          ? parseFloat(plan.monthly_list_price)
          : parseFloat(String(plan.monthly_list_price));
      return `$${price.toFixed(0)}/MO`;
    }
    return '$0/MO';
  }

  /**
   * Get plan description
   */
  getPlanDescription(plan: Package): string {
    // Default descriptions based on plan type
    if (this.isBasicPlan(plan)) {
      return 'The perfect plan for simple, regular washes.';
    } else if (this.isPremiumPlan(plan)) {
      return 'Extra care and shine with added benefits.';
    } else if (this.isPlatinumPlan(plan)) {
      return 'All-inclusive protection for your car all year.';
    }
    return '';
  }

  /**
   * Get plan color class - shuffles between basic (blue), premium (yellow), and platinum (green)
   * This assigns one of the three gradient colors to each card
   */
  getPlanColorClass(plan: Package, index: number): 'basic' | 'premium' | 'platinum' {
    // First check if plan name matches a specific type
    const name = this.getPlanName(plan).toLowerCase();
    if (name.includes('platinum')) {
      return 'platinum';
    } else if (name.includes('premium')) {
      return 'premium';
    } else if (name.includes('basic')) {
      return 'basic';
    }

    // Otherwise, shuffle colors based on index (cycle through: basic, premium, platinum)
    const colorIndex = index % 3;
    if (colorIndex === 0) return 'basic'; // Blue gradient
    if (colorIndex === 1) return 'premium'; // Yellow gradient
    return 'platinum'; // Green gradient
  }

  /**
   * Check if plan is Basic
   */
  isBasicPlan(plan: Package): boolean {
    const name = this.getPlanName(plan).toLowerCase();
    return name.includes('basic');
  }

  /**
   * Check if plan is Premium
   */
  isPremiumPlan(plan: Package): boolean {
    const name = this.getPlanName(plan).toLowerCase();
    return name.includes('premium');
  }

  /**
   * Check if plan is Platinum
   */
  isPlatinumPlan(plan: Package): boolean {
    const name = this.getPlanName(plan).toLowerCase();
    return name.includes('platinum');
  }

  /**
   * Get plan features
   */
  getPlanFeatures(plan: Package): string[] {
    if (plan.features && Array.isArray(plan.features)) {
      return plan.features;
    }
    return [];
  }

  /**
   * Get features title based on plan type and index
   */
  getFeaturesTitle(plan: Package, index: number): string {
    if (index === 0 || this.isBasicPlan(plan)) {
      return 'Key Features:';
    } else if (this.isPremiumPlan(plan)) {
      return 'Everything in Basic, plus:';
    } else if (this.isPlatinumPlan(plan)) {
      return 'Everything in Premium, plus:';
    }
    return 'Key Features:';
  }

  /**
   * Get button label
   */
  getButtonLabel(): string {
    if (this.membershipPlansSectionData?.button?.label) {
      return this.membershipPlansSectionData.button.label;
    }
    return 'Purchase Membership';
  }

  /**
   * Get comparison title
   */
  getComparisonTitle(): string {
    if (this.comparisonSectionData?.title?.label) {
      return this.comparisonSectionData.title.label;
    } else if (this.comparisonSectionData?.title) {
      return this.comparisonSectionData.title;
    }
    return 'Compare Features';
  }

  /**
   * Get comparison features from membership plans
   */
  getComparisonFeatures(): any[] {
    if (!this.comparisonData) {
      // If comparisonData is not set, generate it from membership plans
      this.generateComparisonData();
    }

    if (!this.comparisonData) return [];

    // If comparisonData is an array, return it
    if (Array.isArray(this.comparisonData)) {
      return this.comparisonData;
    }

    // If comparisonData is an object with features array
    if (this.comparisonData.features && Array.isArray(this.comparisonData.features)) {
      return this.comparisonData.features.map((feature: any) => ({
        name: feature.name || feature.feature_name || feature.label || '',
        key:
          feature.key ||
          feature.feature_key ||
          feature.name?.toLowerCase().replace(/\s+/g, '_') ||
          '',
        type: feature.type || 'text',
      }));
    }

    return [];
  }

  /**
   * Get feature value for a specific plan
   */
  getFeatureValue(plan: Package, featureKey: string): string {
    if (!plan || !featureKey) return 'not_included';

    // Normalize feature key for comparison
    const normalizedKey = featureKey.toLowerCase().replace(/\s+/g, '_');

    // Check if plan has this feature in the features array
    if (plan.features && Array.isArray(plan.features)) {
      const feature = plan.features.find((f: string) => {
        if (typeof f === 'string') {
          const normalizedFeature = f.toLowerCase().replace(/\s+/g, '_');
          return normalizedFeature === normalizedKey || normalizedFeature.includes(normalizedKey);
        }
        return false;
      });
      if (feature) {
        return 'included';
      }
    }

    // Check for specific feature keys in the plan object
    if (plan[featureKey as keyof Package] !== undefined) {
      return String(plan[featureKey as keyof Package]);
    }

    // Check for wash count related features
    if (normalizedKey.includes('wash') || normalizedKey.includes('frequency')) {
      if (plan.wash_count_frequency) {
        return `${plan.wash_count_frequency} Washes`;
      }
    }

    // Check for family plan
    if (normalizedKey.includes('family')) {
      return plan.family_plan ? 'included' : 'not_included';
    }

    return 'not_included';
  }

  /**
   * Get membership plans element
   */
  getMembershipPlansElement(): WebsiteElement | undefined {
    return this.elements.find(
      el => el.id === 'membership_plans' || (el as any).originalKey === 'membership_plans_section'
    );
  }

  /**
   * Get comparison element
   */
  getComparisonElement(): WebsiteElement | undefined {
    return this.elements.find(
      el => el.id === 'comparison' || (el as any).originalKey === 'comparison_section'
    );
  }

  /**
   * Handle membership plans section click
   */
  onMembershipPlansSectionClick(event: Event): void {
    event.stopPropagation();
    const element: WebsiteElement = {
      id: 'membership_plans',
      type: 'packages',
      content: this.membershipPlansSectionData || {},
      selected: true,
      originalKey: 'membership_plans_section',
    } as any;
    this.elementSelect.emit(element);
  }

  /**
   * Handle comparison section click
   */
  onComparisonSectionClick(event: Event): void {
    const target = event.target as HTMLElement;

    // Don't handle section click if clicking on scrollable content, buttons, or interactive elements
    if (
      target.closest('.comparison-scroll-wrapper') ||
      target.closest('.comparison-table-wrapper') ||
      target.closest('.comparison-table') ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input') ||
      target.closest('select') ||
      target.closest('textarea') ||
      target.closest('.purchase-button-small') ||
      target.closest('.plan-header-cell')
    ) {
      return; // Let scroll/interactive elements handle the interaction
    }

    event.stopPropagation();
    const element: WebsiteElement = {
      id: 'comparison',
      type: 'section',
      content: this.comparisonSectionData || {},
      selected: true,
      originalKey: 'comparison_section',
    } as any;
    this.elementSelect.emit(element);
  }

  /**
   * Get title styles for ngStyle binding
   */
  getTitleStyles(): any {
    // Check page-level title first (highest priority)
    // Handle both editor and preview formats
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    if (page?.title && typeof page.title === 'object' && page.title.style?.text_style) {
      const titleStyleObj = page.title.style.text_style;
      const styles: any = {};
      if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
      if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
      if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
      if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;
      return styles;
    }

    // Fallback to section-level title
    if (this.pageTitleData?.title && typeof this.pageTitleData.title === 'object') {
      const title = this.pageTitleData.title;
      const titleStyleObj = title.style?.text_style;

      if (titleStyleObj && typeof titleStyleObj === 'object') {
        const styles: any = {};
        if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
        if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
        if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
        if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;
        return styles;
      }
    }

    return {};
  }

  /**
   * Handle package section click
   */
  onPackageSectionClick(event: Event): void {
    // Stop event propagation to prevent conflicts
    event.stopPropagation();

    const packageElement: WebsiteElement = {
      id: 'packages',
      type: 'packages',
      content: {
        ...(this.membershipPlansSectionData || {}),
        title: this.getMembershipPlansTitle(),
        subtitle: this.getMembershipPlansSubtitle(),
        pageTitleData: this.pageTitleData,
      },
      selected: true,
      originalKey: 'package_section',
    } as any;

    this.logger.debug('📦 Package section clicked, emitting element:', packageElement);
    this.elementSelect.emit(packageElement);
  }

  /**
   * Resolve and return the header/logo object by checking header element content first, then page branding fallbacks.
   * @example
   * etHeaderLogo()
   * { image: 'https://example.com/logo.png', text: 'Logo', link: '/', alt: 'Logo' }
   * @param {{void}} {{none}} - No arguments.
   * @returns {{Logo}} Return the Logo object with image, text, link and alt properties.
   **/
  getHeaderLogo(): Logo {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    // Priority 1: Check header element content (most up-to-date)
    if (headerContent?.logo) {
      // If logo has image/url, return it
      if (headerContent.logo.image || headerContent.logo.url) {
        return {
          image: headerContent.logo.image || headerContent.logo.url || '',
          text: headerContent.logo.text || 'Logo',
          link: headerContent.logo.link || '/',
          alt: headerContent.logo.alt || 'Logo',
        };
      }
      // If logo has text but no image, return text logo
      if (headerContent.logo.text) {
        return {
          image: '',
          text: headerContent.logo.text,
          link: headerContent.logo.link || '/',
          alt: headerContent.logo.alt || 'Logo',
        };
      }
    }
    // Priority 2: Check branding data from pageData
    if (this.pageData?.json_data?.website?.branding?.logo) {
      const brandingLogo = this.pageData.json_data.website.branding.logo;
      if (brandingLogo.url || brandingLogo.image) {
        return {
          image: brandingLogo.url || brandingLogo.image || '',
          text: brandingLogo.text || 'Logo',
          link: brandingLogo.link || '/',
          alt: brandingLogo.alt || 'Logo',
        };
      }
      if (brandingLogo.text) {
        return {
          image: '',
          text: brandingLogo.text,
          link: brandingLogo.link || '/',
          alt: brandingLogo.alt || 'Logo',
        };
      }
    }
    return { text: 'Logo', link: '/', image: '', alt: 'Logo' };
  }

  /**
   * Build and return the header navigation items from the header content or fallback to pages, inferring and normalizing each item's pageType when missing.
   * @example
   * etHeaderNavigation()
   * [{ label: 'Home', link: '#home', pageType: 'home', active: true }]
   * @returns {{NavigationItem[]}} Array of navigation items with pageType, link, label and optional active flag.
   **/
  getHeaderNavigation(): NavigationItem[] {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.navigation && Array.isArray(headerContent.navigation)) {
      // Ensure navigation items have pageType set
      return headerContent.navigation.map((nav: any) => {
        // If pageType is not set, try to extract it from link or find matching page
        if (!nav.pageType) {
          const matchingPage = this.pages?.find(
            (p: any) =>
              p.name === nav.label ||
              (p as any).pageType === nav.link?.replace(/^#+|\/+$/g, '').replace(/^\/+/, '')
          );
          if (matchingPage) {
            nav.pageType = (matchingPage as any).pageType;
          } else {
            // Extract from link
            const normalizedLink = (nav.link || '')
              .replace(/^#+|\/+$/g, '')
              .replace(/^\/+/, '')
              .toLowerCase();
            if (
              normalizedLink === 'expresswashes' ||
              normalizedLink === 'express-washes' ||
              normalizedLink.includes('express-wash')
            ) {
              nav.pageType = 'express-wash-packages';
            } else if (
              normalizedLink === 'expressdetails' ||
              normalizedLink === 'express-details' ||
              normalizedLink.includes('express-detail') ||
              normalizedLink.includes('express-auto')
            ) {
              nav.pageType = 'express-auto-details';
            } else if (
              normalizedLink === 'fleets' ||
              normalizedLink === 'fleet' ||
              normalizedLink.includes('fleet')
            ) {
              nav.pageType = 'fleet-management';
            } else if (
              normalizedLink === 'coupons' ||
              normalizedLink === 'coupon' ||
              normalizedLink.includes('coupon')
            ) {
              nav.pageType = 'coupons';
            } else if (
              normalizedLink === '' ||
              normalizedLink === 'home' ||
              normalizedLink === '/'
            ) {
              nav.pageType = 'home';
            } else {
              nav.pageType = normalizedLink || 'home';
            }
          }
        }
        return nav;
      });
    }
    // Fallback to generating from pages
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      return activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        pageType: (page as any).pageType || 'home', // Ensure pageType is set
        active: page.pageType === this.currentPageType,
      }));
    }
    return [];
  }

  /**
   * Get the header call-to-action (CTA) button configuration from the component header content, applying sensible defaults and fallbacks.
   * @example
   * etHeaderCTAButton()
   * { text: 'Book Wash', link: '#', backgroundColor: '#1B5BFF', textColor: '#FFFFFF' }
   * @returns {{CTAButton | null}} CTA button configuration object with properties text, link, backgroundColor and textColor, or null if the header is not available.
   **/
  getHeaderCTAButton(): CTAButton | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    const primaryColor = this.getPrimaryColor();
    if (headerContent?.ctaButton) {
      return {
        text: headerContent.ctaButton.text || headerContent.ctaButton.label || 'Book Wash',
        link: headerContent.ctaButton.link || '#',
        // Use button's backgroundColor if provided, otherwise fall back to primaryColor
        backgroundColor: headerContent.ctaButton.backgroundColor || primaryColor || '#1B5BFF',
        textColor: headerContent.ctaButton.textColor || '#FFFFFF',
      };
    }
    return {
      text: 'Book Wash',
      link: '#',
      backgroundColor: primaryColor,
      textColor: '#FFFFFF',
    };
  }

  getHeaderBackgroundColor(): string | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.backgroundColor) {
      return headerContent.backgroundColor as string;
    }
    return null;
  }

  /**
   * Get primary color from theme or use default
   */
  getPrimaryColor(): string {
    return this.primaryColor || '#1B5BFF';
  }

  /**
   * Get heading color from branding or use default
   */
  getHeadingColor(): string {
    return this.headingColor || '#15172E';
  }

  /**
   * Get paragraph color from branding or use default
   */
  getParagraphColor(): string {
    return this.paragraphColor || '#6B7280';
  }

  /**
   * Get heading font from branding or use default
   */
  getHeadingFont(): string {
    return this.headingFont || 'Manrope';
  }

  /**
   * Get body font from branding or use default
   */
  getBodyFont(): string {
    return this.bodyFont || 'Inter';
  }

  /**
   * Get footer quick links from active pages
   * Filters out pages where visible === false (active === false)
   */
  getFooterQuickLinks(): { label: string; link: string }[] {
    // First, try to get from footer element content
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const storedLinks = Array.isArray(footerContent?.quickLinks) ? footerContent.quickLinks : [];
    return resolveFooterQuickLinks(storedLinks, this.pages);
  }

  /**
   * Get normalized social links from the component footer, supporting multiple footer data shapes.
   * @example
   * etFooterSocialLinks()
   * [{ platform: 'facebook', url: 'https://facebook.com/example', icon: 'facebook' }]
   * @param {Object} context - The component instance (this) used to access footer content via getFooterElement().
   * @returns {Array<{ platform: string; url: string; icon: string }>} Normalized array of social link objects.
   **/
  getFooterSocialLinks(): { platform: string; url: string; icon: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;

    // Handle different data structures:
    // 1. socialLinks as array (from website builder)
    if (footerContent?.socialLinks && Array.isArray(footerContent.socialLinks)) {
      return footerContent.socialLinks.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }

    // 2. socialLinks as object with platforms array (from website preview)
    if (
      footerContent?.socialLinks?.platforms &&
      Array.isArray(footerContent.socialLinks.platforms)
    ) {
      return footerContent.socialLinks.platforms.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }

    // 3. Check for social_links.platforms (alternative structure)
    if (
      footerContent?.social_links?.platforms &&
      Array.isArray(footerContent.social_links.platforms)
    ) {
      return footerContent.social_links.platforms.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }

    // 4. Check for social_links as array
    if (footerContent?.social_links && Array.isArray(footerContent.social_links)) {
      return footerContent.social_links.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }

    return [];
  }

  getFooterSocialLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const title = footerContent?.socialLinksTitle;
    if (typeof title === 'string') {
      return title;
    } else if (title && typeof title === 'object' && title.text) {
      return title.text;
    }
    return 'Connect with us';
  }

  getFooterQuickLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const title = footerContent?.quickLinksTitle;
    if (typeof title === 'string') {
      return title;
    } else if (title && typeof title === 'object' && title.text) {
      return title.text;
    }
    return 'Quick links';
  }

  getFooterCopyright(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const copyright = footerContent?.copyright;
    if (typeof copyright === 'string') {
      return copyright;
    } else if (copyright && typeof copyright === 'object' && copyright.text) {
      return copyright.text;
    }
    return `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;
  }

  /**
   * Get footer logo from footer content, falling back to header logo or defaults.
   * @example
   * etFooterLogo()
   * { text: 'way', image: 'https://example.com/logo.png' }
   * @param {{void}} none - No parameters.
   * @returns {{ { text?: string; image?: string } | undefined }} Returns an object with optional text and image properties, or undefined.
   **/
  getFooterLogo(): { text?: string; image?: string } | undefined {
    // Get logo from footer element content
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;

    if (footerContent?.logo) {
      const logo = footerContent.logo;
      return {
        text: logo.text || 'way',
        image: logo.image || logo.url || '',
      };
    }

    // Fallback to header logo if footer logo not available
    const headerLogo = this.getHeaderLogo();
    if (headerLogo && headerLogo.image) {
      return {
        text: headerLogo.text,
        image: headerLogo.image,
      };
    }
    return { text: 'way' };
  }

  getFooterBackgroundColor(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.backgroundColor) {
      return footerContent.backgroundColor;
    }
    return '#000920';
  }

  getHeaderElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'header');
  }

  getFooterElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'footer');
  }

  /**
   * Handle header click events: stop propagation for navigation, CTA, hamburger or mobile menu elements and invoke header click handling when appropriate.
   * @example
   * nHeaderClick(new MouseEvent('click'))
   * undefined
   * @param {{Event}} {{event}} - The click event to process on the header or document.
   * @returns {{void}} Return nothing; may stop propagation or call this.onElementClick for the header element.
   **/
  onHeaderClick(event: Event): void {
    // Don't propagate if clicking on navigation items, CTA button, or hamburger menu
    const target = event.target as HTMLElement;
    // Don't handle clicks if mobile menu is open or clicking on mobile menu elements
    if (
      target.closest('.nav-link') ||
      target.closest('.book-wash-btn') ||
      target.closest('.hamburger-menu') ||
      target.closest('.mobile-menu-overlay') ||
      target.closest('.mobile-menu-drawer') ||
      target.closest('.mobile-navigation') ||
      target.closest('.mobile-nav-link') ||
      document.querySelector('.mobile-menu-drawer.open')
    ) {
      event.stopPropagation();
      return;
    }

    const headerElement = this.getHeaderElement();
    if (headerElement) {
      this.onElementClick(headerElement);
    }
  }

  onElementClick(element: WebsiteElement): void {
    if (!element) return;
    this.elementSelect.emit(element);
  }

  /**
   * Handle footer click: stops event propagation, ensures the footer element has type and id set to 'footer', and delegates the element to onElementClick.
   * @example
   * nFooterClick(new Event('click'))
   * undefined
   * @param {Event} event - The click event to handle; its propagation will be stopped.
   * @returns {void} No return value.
   **/
  onFooterClick(event: Event): void {
    event.stopPropagation();
    const footerElement = this.getFooterElement();
    if (footerElement) {
      // Ensure footer element has correct type and id
      if (!footerElement.type || footerElement.type !== 'footer') {
        footerElement.type = 'footer';
      }
      if (!footerElement.id || footerElement.id !== 'footer') {
        footerElement.id = 'footer';
      }
      this.onElementClick(footerElement);
    }
  }

  onNavigationClick(pageType: string): void {
    this.navigationClick.emit(pageType);
  }

  /**
   * Handle Buy Now / Purchase Membership button click
   */
  onBuyNowClick(plan: Package, event: Event): void {
    event.stopPropagation();
    this.selectedPackageForModal = plan;
    this.showPackageSelectionModal = true;
  }

  /**
   * Close package selection modal
   */
  onClosePackageModal(): void {
    this.showPackageSelectionModal = false;
    this.selectedPackageForModal = null;
  }

  /**
   * Handle package selection from modal
   */
  onPackageSelected(selected: SelectedPackage): void {
    this.logger.debug('Package selected:', selected);
    // Handle the selected package here
    // You can emit an event or perform any action needed
  }

  /**
   * Get packages for modal (returns selected package or all packages)
   */
  getPackagesForModal(): Package[] {
    if (this.selectedPackageForModal) {
      return [this.selectedPackageForModal];
    }
    return this.membershipPlans;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
